import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "../../contexts/SearchContext";
import { Modal, message } from "antd";
import PassengerData from "./PassengerData";
import { useSelectedResult } from "../../contexts/SelectedResultContext";
import { showSuccessToast } from "../../services/swalService";
import {
  passengersToClipboard,
  pastePassengersIntoForm,
  getPassengersClipboard,
  setPassengersClipboard,
  hasFilledPassengerData,
} from "../../utils/passengersClipboard";
import "./PassengersData.scss";

export { PASSENGER_KEYS, ROOM_KEYS } from "./passengerKeys";

const PassengersData = ({ allDisabled, returnDate, isTour = false, isCustomerLink = false }) => {
  const { t } = useTranslation();
  const selectedResultContext = useSelectedResult();
  const { roomsAndPaxInfo, setRoomsAndPaxInfo, fieldsFilled, ldsPassengerFieldErrors } = selectedResultContext || { 
    roomsAndPaxInfo: [], 
    setRoomsAndPaxInfo: () => {}, 
    fieldsFilled: false,
    ldsPassengerFieldErrors: {},
  };
  const normalizedRoomsInput = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const renderedRooms = normalizedRoomsInput;
  const prevFieldsFilledRef = useRef(false);
  const filledFieldsCountRef = useRef(0);
  const toastCountRef = useRef(0);
  const totalPassengersRef = useRef(0);

  function handlePassengerInfoChange(roomIndex, newPassengerInfo) {
    if (!roomsAndPaxInfo || !setRoomsAndPaxInfo) return;
    let tempPassengerInfo = JSON.parse(JSON.stringify(roomsAndPaxInfo));
    tempPassengerInfo[roomIndex] = newPassengerInfo;
    setRoomsAndPaxInfo(tempPassengerInfo);
    
    // Show encouragement messages for customer (max 2 per passenger)
    if (isCustomerLink && !allDisabled) {
      // Count total passengers and filled fields
      const totalPassengers = countTotalPassengers(tempPassengerInfo);
      const filledFields = countFilledFields(tempPassengerInfo);
      const totalFields = totalPassengers * 4; // 4 fields per passenger (firstname, lastname, email, cell)
      
      // Initialize total passengers on first change
      if (totalPassengersRef.current === 0 && totalPassengers > 0) {
        totalPassengersRef.current = totalPassengers;
        toastCountRef.current = 0;
      }
      
      // Calculate max toasts (2 per passenger)
      const maxToasts = totalPassengersRef.current * 2;
      
      // Show toast only if:
      // 1. We haven't reached max toasts
      // 2. We've filled at least 25% of fields (first milestone)
      // 3. Or we've filled at least 75% of fields (second milestone)
      // 4. And we've made progress
      if (filledFields > filledFieldsCountRef.current && toastCountRef.current < maxToasts) {
        const progressPercent = (filledFields / totalFields) * 100;
        const shouldShowToast = 
          (progressPercent >= 25 && filledFieldsCountRef.current < totalFields * 0.25) ||
          (progressPercent >= 75 && filledFieldsCountRef.current < totalFields * 0.75);
        
        if (shouldShowToast) {
          filledFieldsCountRef.current = filledFields;
          toastCountRef.current++;
          const messages = [
            "כל הכבוד! אתה מתקדם מצוין 🎉",
            "מעולה! ממשיכים קדימה 💪",
            "נהדר! אתה עושה עבודה מעולה ⭐",
            "פנטסטי! כמעט סיימת 🚀",
            "מצוין! אתה ממלא את הפרטים בקפידה 📝",
            "כל הכבוד! אתה מתקדם יפה מאוד 🌟",
            "מעולה! הפרטים נראים טוב 👍",
            "נהדר! אתה עושה עבודה מקצועית 🎯",
            "פנטסטי! אתה מתקדם מהר 🏃",
            "מצוין! הפרטים מתמלאים יפה ✨",
            "כל הכבוד! אתה עושה את זה נכון 🎊",
            "מעולה! ממשיכים להתקדם 💫",
            "נהדר! אתה ממלא את הפרטים בדיוק 🎨",
            "פנטסטי! אתה מתקדם מצוין 🌈",
            "מצוין! הפרטים נראים מעולים 🏆",
            "כל הכבוד! אתה עושה עבודה נהדרת 🎪",
            "מעולה! אתה מתקדם יפה מאוד 🎭",
            "נהדר! הפרטים מתמלאים מהר 🌺",
            "פנטסטי! אתה עושה את זה מצוין 🦋",
            "מצוין! אתה מתקדם נהדר 🎁",
            "כל הכבוד! הפרטים נראים מושלמים 💎",
            "מעולה! אתה ממלא את הפרטים בחכמה 🧠"
          ];
          const randomMessage = messages[Math.floor(Math.random() * messages.length)];
          showSuccessToast(randomMessage);
        } else {
          filledFieldsCountRef.current = filledFields;
        }
      } else {
        filledFieldsCountRef.current = filledFields;
      }
    }
  }

  function countTotalPassengers(passengerInfo) {
    let count = 0;
    passengerInfo?.forEach((room) => {
      count += room?.adults?.length || 0;
      count += room?.kids?.length || 0;
    });
    return count;
  }

  function countFilledFields(passengerInfo) {
    let count = 0;
    passengerInfo?.forEach((room) => {
      room?.adults?.forEach((adult) => {
        if (adult?.firstname) count++;
        if (adult?.lastname) count++;
        if (adult?.email) count++;
        if (adult?.cell) count++;
      });
      room?.kids?.forEach((kid) => {
        if (kid?.firstname) count++;
        if (kid?.lastname) count++;
      });
    });
    return count;
  }

  // Agent-only: copy/paste passengers (localStorage, no backend)
  const [hasClipboard, setHasClipboard] = useState(() => !!getPassengersClipboard()?.rooms?.length);

  const handleCopyPassengers = () => {
    const data = passengersToClipboard(roomsAndPaxInfo);
    if (!data || !data.rooms?.length) {
      message.warning("אין פרטי נוסעים להעתקה");
      return;
    }
    setPassengersClipboard(data);
    setHasClipboard(true);
    message.success("פרטי הנוסעים הועתקו. ניתן להדביק בחבילה אחרת.");
  };

  const handlePastePassengers = () => {
    const data = getPassengersClipboard();
    if (!data?.rooms?.length) {
      message.info("אין נתונים שמורים להדבקה. העתק תחילה פרטי נוסעים.");
      return;
    }
    const doPaste = () => {
      const next = pastePassengersIntoForm(data, roomsAndPaxInfo);
      setRoomsAndPaxInfo(next);
      message.success("פרטי הנוסעים הודבקו.");
    };
    if (hasFilledPassengerData(roomsAndPaxInfo)) {
      Modal.confirm({
        title: "הדבקת פרטי נוסעים",
        content: "הדבקה תדרוס את הנתונים הקיימים בטופס. להמשיך?",
        okText: "הדבק",
        cancelText: "ביטול",
        onOk: doPaste,
      });
    } else {
      doPaste();
    }
  };

  // Note: Autofill detection is now handled in SelectedResultContext
  // which periodically checks roomsAndPaxInfo and updates fieldsFilled

  useEffect(() => {
    console.log("[PROFILE_PASSENGER_FORM_ROOMS_INPUT]", {
      roomsCount: normalizedRoomsInput.length,
      rooms: normalizedRoomsInput.map((room, idx) => ({
        idx,
        roomId:
          room?.roomId ??
          room?.room?.roomId ??
          room?.room?.["@seqNum"] ??
          null,
        roomName: room?.roomName ?? room?.room?.roomType ?? null,
        adults: Array.isArray(room?.adults) ? room.adults.length : 0,
        kids: Array.isArray(room?.kids) ? room.kids.length : 0,
        infants: Array.isArray(room?.infants) ? room.infants.length : 0,
      })),
    });
  }, [normalizedRoomsInput]);

  useEffect(() => {
    console.log("[PROFILE_PASSENGER_FORM_RENDERED_ROOMS]", {
      renderedCount: renderedRooms.length,
      renderedRooms: renderedRooms.map((room, idx) => ({
        idx,
        roomId:
          room?.roomId ??
          room?.room?.roomId ??
          room?.room?.["@seqNum"] ??
          null,
        roomName: room?.roomName ?? room?.room?.roomType ?? null,
      })),
    });
    if (normalizedRoomsInput.length > 1 && renderedRooms.length < normalizedRoomsInput.length) {
      console.warn("[PROFILE_PASSENGER_FORM_ROOM_COUNT_MISMATCH]", {
        expected: normalizedRoomsInput.length,
        rendered: renderedRooms.length,
      });
    }
  }, [renderedRooms, normalizedRoomsInput]);

  // Show success message when all fields are filled
  useEffect(() => {
    if (isCustomerLink && fieldsFilled && !prevFieldsFilledRef.current) {
      prevFieldsFilledRef.current = true;
      const completionMessages = [
        "מצוין! כל הפרטים מולאו בהצלחה 🎊",
        "כל הכבוד! סיימת למלא את כל הפרטים 🎉",
        "מעולה! כל הפרטים מוכנים ⭐",
        "פנטסטי! סיימת את שלב מילוי הפרטים 🚀",
        "נהדר! כל הפרטים מולאו בהצלחה 💪",
        "מצוין! אתה מוכן לשלב הבא ✨",
        "כל הכבוד! סיימת למלא את הפרטים 🏆",
        "מעולה! כל הפרטים מוכנים להמשך 🌟",
        "פנטסטי! סיימת את שלב הפרטים 🎯",
        "נהדר! כל הפרטים מולאו יפה מאוד 💎",
        "מצוין! אתה מוכן להמשיך 🎨",
        "כל הכבוד! סיימת למלא את כל הפרטים 🎪",
        "מעולה! הפרטים מוכנים להמשך 🎭",
        "פנטסטי! סיימת את שלב הפרטים בהצלחה 🌈",
        "נהדר! כל הפרטים מולאו מעולה 🦋",
        "מצוין! אתה מוכן לשלב הבא 🎁",
        "כל הכבוד! סיימת למלא את הפרטים 🧠",
        "מעולה! כל הפרטים מוכנים 🌺",
        "פנטסטי! סיימת את שלב הפרטים 🎊",
        "נהדר! כל הפרטים מולאו בהצלחה מלאה 💫"
      ];
      const randomMessage = completionMessages[Math.floor(Math.random() * completionMessages.length)];
      showSuccessToast(randomMessage);
    }
  }, [fieldsFilled, isCustomerLink]);

  return (
    <div className="passengers-data">
      {!isCustomerLink && (
        <div className="passengers-data-clipboard-toolbar">
          <button
            type="button"
            className="passengers-data-clipboard-btn passengers-data-clipboard-btn-copy"
            onClick={handleCopyPassengers}
            disabled={allDisabled || !roomsAndPaxInfo?.length}
          >
            העתק נוסעים
          </button>
          <button
            type="button"
            className="passengers-data-clipboard-btn passengers-data-clipboard-btn-paste"
            onClick={handlePastePassengers}
            disabled={allDisabled || !hasClipboard}
            title={!hasClipboard ? "אין נתונים שמורים. העתק תחילה פרטי נוסעים." : undefined}
          >
            הדבק נוסעים
          </button>
          {!hasClipboard && (
            <span className="passengers-data-clipboard-hint">אין נתונים להדבקה. העתק תחילה.</span>
          )}
        </div>
      )}
      <div className="passengerData-room-pass-info-wrapper">
        {renderedRooms.map((passengerInfo, roomIndex) => (
          <PassengerData
            key={passengerInfo?.["@seqNum"] ?? `room-${roomIndex}`}
            roomIndex={roomIndex}
            passengerInfo={passengerInfo}
            setPassengerInfo={(newPassengerInfo) =>
              handlePassengerInfoChange(roomIndex, newPassengerInfo)
            }
            allDisabled={allDisabled}
            roomName={`(${passengerInfo?.room?.roomSet}) ${passengerInfo?.room?.roomType}`}
            returnDate={returnDate}
            isTour={isTour}
            useGenderDerivedLdsTitles
            fieldErrors={ldsPassengerFieldErrors}
          />
        )) || null}
      </div>
    </div>
  );
};

export default PassengersData;
